// src/pages/FindDoctorPage/DoctorFinder/DoctorFinder.jsx

"use client";

import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import SearchBarGroup from "../../../DoctorFinder/components/FindDoctorPage/SearchBarGroup/SearchBarGroup";
import MapSection from "../../../DoctorFinder/components/FindDoctorPage/MapSection/MapSection";
import DoctorListView from "../../../DoctorFinder/components/FindDoctorPage/DoctorListView/DoctorListView";
import FollowedDoctors from "../../../DoctorFinder/components/FollowedDoctors/FollowedDoctors";

import AppLayout from "../../../navigation/Layout/Layout";

import useProximityDoctors from "../../../DoctorFinder/hooks/useProximityDoctors";
import useAuthSession from "../../../authPatient/hooks/useAuthSession";
import { useUserInformation } from "../../../Profile/hooks/useUserInformation";

import { fetchDoctors } from "../../../authDoctor/redux/thunks/doctorsThunks";
import {
  selectDoctors,
  selectDoctorLoading,
  selectDoctorError,
} from "../../../authDoctor/redux/selectors/doctorSelectors";

import "./DoctorFinder.css";

/* =========================================================
   HELPERS
========================================================= */

const ALL_SPECIALTIES = "all specialties";

const norm = (value) => String(value || "").trim().toLowerCase();

const getSavedSearch = () => {
  try {
    const saved = sessionStorage.getItem("pendingDoctorSearch");
    if (saved) {
      sessionStorage.removeItem("pendingDoctorSearch");
      return JSON.parse(saved);
    }
  } catch (error) {
    console.warn("[DoctorFinder] Unable to read pendingDoctorSearch", error);
  }
  return null;
};

const getCoords = (doctor) => {
  const lat =
    doctor?.location?.lat ??
    doctor?.lat ??
    doctor?.coordinates?.lat ??
    doctor?.raw?.location?.lat ??
    doctor?.raw?.lat ??
    null;

  const lng =
    doctor?.location?.lng ??
    doctor?.lng ??
    doctor?.coordinates?.lng ??
    doctor?.raw?.location?.lng ??
    doctor?.raw?.lng ??
    null;

  return { lat, lng };
};

// Normalise un médecin MediLink (Redux) vers le format attendu par la carte / liste
const normalizeMedilinkDoctor = (doctor) => {
  const { lat, lng } = getCoords(doctor);
  if (lat == null || lng == null) return null;

  const name =
    doctor?.fullName ||
    doctor?.name ||
    `${doctor?.firstName || ""} ${doctor?.lastName || ""}`.trim() ||
    doctor?.raw?.fullName ||
    "";

  const id = String(
    doctor?.id ?? doctor?._id ?? doctor?.doctorId ?? doctor?.userId ?? ""
  );

  const city = doctor?.city || doctor?.raw?.city || "";
  const address = doctor?.clinicAddress || doctor?.raw?.clinicAddress || "";

  return {
    id,
    name,
    specialty: doctor?.specialty || doctor?.raw?.specialty || "Doctor",
    address: address || city,
    city,
    phone: doctor?.phone || doctor?.raw?.phone || "",
    governorate: city,
    rating: null,
    is_open_now: null,
    distanceText: "",
    lat,
    lng,
    avatar:
      doctor?.avatar ||
      doctor?.photo ||
      doctor?.raw?.avatar ||
      "/src/assets/images/doctor-placeholder.png",
    google_maps_url: "",
    email: norm(doctor?.email || doctor?.raw?.email),
    isMedilinker: true,
    medilinkerData: doctor,
    medilinkerAccountId: id,
  };
};

// Même logique de filtre pour tous les médecins (MediLink + externes)
const matchesFilters = (doctor, { query, location, specialty }) => {
  if (specialty && specialty !== ALL_SPECIALTIES) {
    if (!norm(doctor.specialty).includes(specialty)) return false;
  }

  if (query) {
    const text = norm(
      `${doctor.name} ${doctor.specialty} ${doctor.city || ""} ${doctor.address}`
    );
    if (!text.includes(query)) return false;
  }

  if (location) {
    const text = norm(
      `${doctor.city || ""} ${doctor.address} ${doctor.governorate || ""}`
    );
    if (!text.includes(location)) return false;
  }

  return true;
};

/* =========================================================
   COMPONENT
========================================================= */

const DoctorFinder = ({ currentUser: currentUserProp }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  /* ---------- RTL (comme Appointments) ---------- */
  const isRTL = i18n.language?.startsWith("ar");

  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
  }, [isRTL]);

  /* ---------- USER ---------- */
  const { auth } = useAuthSession({ auto: true, once: false });
  const user = auth?.user || currentUserProp || null;
  const { avatarSrc } = useUserInformation(user);

  /* ---------- MEDILINK DOCTORS (Redux, comme Appointments) ---------- */
  const medilinkerList = useSelector(selectDoctors);
  const medilinkLoading = useSelector(selectDoctorLoading);
  const medilinkError = useSelector(selectDoctorError);

  useEffect(() => {
    dispatch(fetchDoctors());
  }, [dispatch]);

  const retryLoadDoctors = useCallback(() => {
    dispatch(fetchDoctors());
  }, [dispatch]);

  const refreshPage = useCallback(() => {
    window.location.reload();
  }, []);

  /* ---------- SAVED SEARCH ---------- */
  const savedSearch = useMemo(() => getSavedSearch(), []);

  /* ---------- STATE ---------- */
  const [searchQuery, setSearchQuery] = useState(savedSearch?.searchQuery || "");
  const [location, setLocation] = useState(savedSearch?.location || "");
  const [specialty, setSpecialty] = useState(
    savedSearch?.specialty || "All specialties"
  );
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [focusLocation, setFocusLocation] = useState(null);
  const [viewMode, setViewMode] = useState("map");

  /* ---------- PROXIMITY (uniquement Near me + médecins externes) ---------- */
  const {
    doctors: externalDoctors,
    loading: proximityLoading,
    error: proximityError,
    requestUserLocationAndNearby,
    userLocation,
    radiusKm,
  } = useProximityDoctors();

  // Chargement initial des médecins à proximité
  useEffect(() => {
    requestUserLocationAndNearby({
      specialty: "All specialties",
      governorate: "",
      radius: radiusKm,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- FILTRAGE LOCAL (même principe que Appointments) ---------- */
  const filteredDoctors = useMemo(() => {
    const filters = {
      query: norm(searchQuery),
      location: norm(location),
      specialty: norm(specialty),
    };

    const medilinkDoctors = (Array.isArray(medilinkerList) ? medilinkerList : [])
      .map(normalizeMedilinkDoctor)
      .filter(Boolean)
      .filter((doctor) => matchesFilters(doctor, filters));

    const medilinkEmails = new Set(
      medilinkDoctors.map((doctor) => doctor.email).filter(Boolean)
    );

    const externals = (externalDoctors || [])
      .map((doctor) => ({ ...doctor, isMedilinker: false }))
      .filter((doctor) => matchesFilters(doctor, filters))
      .filter((doctor) => !medilinkEmails.has(norm(doctor?.email)));

    return [...medilinkDoctors, ...externals];
  }, [medilinkerList, externalDoctors, searchQuery, location, specialty]);

  /* ---------- FOCUS SUR LE 1er MÉDECIN MEDILINK ---------- */
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFocusLocation(null);
      return;
    }

    const first = filteredDoctors.find(
      (doctor) => doctor.isMedilinker && doctor.lat != null && doctor.lng != null
    );
    if (!first) return;

    setFocusLocation((previous) =>
      previous?.latitude === first.lat && previous?.longitude === first.lng
        ? previous
        : { latitude: first.lat, longitude: first.lng }
    );
  }, [filteredDoctors, searchQuery]);

  /* ---------- ACTIONS ---------- */

  // Le filtrage est déjà instantané : le bouton Search recentre simplement la carte
  const handleSearch = useCallback(() => {
    const first = filteredDoctors.find(
      (doctor) => doctor.lat != null && doctor.lng != null
    );
    setFocusLocation(
      first ? { latitude: first.lat, longitude: first.lng } : null
    );
  }, [filteredDoctors]);

  const handleNearMe = useCallback(() => {
    setFocusLocation(null);
    requestUserLocationAndNearby({
      specialty,
      governorate: location,
      radius: radiusKm,
    });
  }, [specialty, location, radiusKm, requestUserLocationAndNearby]);

  const loading = medilinkLoading || proximityLoading;

  // Erreur affichée seulement s'il n'y a aucun médecin à montrer
  const displayError =
    filteredDoctors.length > 0 ? null : medilinkError || proximityError;

  /* ---------- ERREUR BLOQUANTE (comme Appointments) ---------- */
  if (medilinkError && !medilinkLoading && filteredDoctors.length === 0) {
    return (
      <AppLayout>
        <div className={`doctor-finder-page ${isRTL ? "is-rtl" : ""}`}>
          <div className="appointments-error-container">
            <div className="appointments-error-content">
              <div className="error-icon">⚠️</div>

              <h2 className="error-title">
                {t(
                  "doctorFinder.errorTitle",
                  "Impossible de charger les médecins"
                )}
              </h2>

              <p className="error-message">
                {t(
                  "doctorFinder.errorLoadingDoctors",
                  "Erreur lors du chargement"
                )}{" "}
                {String(medilinkError || "")}
              </p>

              <p className="error-suggestion">
                {t(
                  "doctorFinder.errorSuggestion",
                  "Veuillez essayer de rafraîchir la page ou de réessayer."
                )}
              </p>

              <div className="error-actions">
                <button className="btn-primary" onClick={retryLoadDoctors}>
                  🔄 {t("doctorFinder.actions.retry", "Réessayer")}
                </button>
                <button className="btn-secondary" onClick={refreshPage}>
                  🔃 {t("doctorFinder.actions.refresh", "Rafraîchir")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  /* ---------- RENDER ---------- */
  return (
    <AppLayout>
      <div className={`doctor-finder-page ${isRTL ? "is-rtl" : ""}`}>
        <div className="doctor-finder-grid">
          {/* LEFT */}
          <div className="doctor-finder-col doctor-finder-col--left">
            <FollowedDoctors limit={8} />
          </div>

          {/* RIGHT */}
          <div className="doctor-finder-col doctor-finder-col--right">
            <section className="doctor-finder-hero">
              {/* HEADER */}
              <div className="doctor-finder-hero__head">
                <div className="doctor-finder-hero__heading">
                  <h1 className="Header-title">
                    {t("doctorFinder.heroTitle")}
                  </h1>
                  <p className="Header-subtitle">
                    {t("doctorFinder.heroSubtitle")}
                  </p>
                </div>

                {/* MAP/LIST SWITCH */}
                <div
                  className="view-toggle"
                  role="group"
                  aria-label={t("doctorFinder.viewToggle", "Toggle view")}
                >
                  <button
                    type="button"
                    className={`view-toggle__btn ${
                      viewMode === "map" ? "is-active" : ""
                    }`}
                    onClick={() => setViewMode("map")}
                    aria-pressed={viewMode === "map"}
                    title={t("doctorFinder.mapView", "Map view")}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M7.5 3.75 3.333 5.417v10.833L7.5 14.583m0-10.833 5 1.666m-5-1.666v10.833m5-9.167 4.167-1.666v10.833L12.5 14.583m0-10.833v10.833m0 0-5-1.667"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  <button
                    type="button"
                    className={`view-toggle__btn ${
                      viewMode === "list" ? "is-active" : ""
                    }`}
                    onClick={() => setViewMode("list")}
                    aria-pressed={viewMode === "list"}
                    title={t("doctorFinder.listView", "List view")}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 20 20"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M7.5 5h9.167M7.5 10h9.167M7.5 15h9.167M3.333 5h.008M3.333 10h.008M3.333 15h.008"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* SEARCH */}
              <SearchBarGroup
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                location={location}
                setLocation={setLocation}
                specialty={specialty}
                setSpecialty={setSpecialty}
                onSearch={handleSearch}
                onNearMe={handleNearMe}
              />

              {/* CONTENT */}
              <div className="doctor-finder-content">
                {loading && filteredDoctors.length === 0 && (
                  <div className="dd-loading">
                    {t("doctorFinder.loadingDoctors", "Chargement des médecins...")}
                  </div>
                )}

                {viewMode === "map" ? (
                  <MapSection
                    searchQuery={searchQuery}
                    location={location}
                    specialty={specialty}
                    selectedDoctor={selectedDoctor}
                    setSelectedDoctor={setSelectedDoctor}
                    doctors={filteredDoctors}
                    userLocation={userLocation}
                    focusLocation={focusLocation}
                    loading={loading}
                    error={displayError}
                    requestLocation={requestUserLocationAndNearby}
                    currentUser={user}
                    avatarSrc={avatarSrc}
                  />
                ) : (
                  <DoctorListView
                    doctors={filteredDoctors}
                    loading={loading}
                    error={displayError}
                  />
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default DoctorFinder;